import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import {
  customElement,
  property,
  queryAssignedElements,
  state,
} from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { createKeyboardNavigationHandler } from '../../internal/keyboard-navigation.js'
import DapDSPopup from '../popup/popup.component.js'
import popupStyles from '../popup/popup.scss?inline'
import styles from './navigation-menu-item.scss?inline'

/**
 * `dap-ds-navigation-menu-item`
 * @summary A navigation menu item that can contain either a simple link or dropdown content.
 * @element dap-ds-navigation-menu-item
 * @title - Navigation Menu Item
 * @group navigation-menu
 *
 * @property {'xs' | 'sm' | 'lg'} size - The size of the popup.
 * @property {boolean} disabled - The disabled state of the popup.
 * @property {boolean} opened - The open state of the popup.
 * @property {'top' | 'right' | 'bottom' | 'left' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end'} placement - The placement of the popup.
 * @property {'absolute' | 'fixed'} floatingStrategy - The floating strategy of the popup.
 * @property {number} offset - The offset of the popup.
 * @property {boolean} sync - Whether the popup should sync its width with the trigger.
 * @property {number | 'auto'} maxHeight - The maximum height of the popup.
 * @property {number | 'auto'} maxWidth - The maximum width of the popup.
 * @property {boolean} hasArrow - Whether the popup has an arrow.
 * @property {boolean} overflow - Whether the popup should overflow.
 * @property {boolean} fullWidth - Whether the popup should take full width of the screen.
 * @property {boolean} exactHref - Whether the navigation item should be active if the href is exactly the same as the activeHref.
 * @property {string} icon - The name of the icon to display in the trigger.
 * @property {string} ariaLabelledBy - The name of the element that labels the navigation dropdown.
 * @property {string} activeHref - The href of the navigation item that is active.
 * @property {string} baseHref - The href of the navigation item. If provided, the navigation item will be active if the href is a substring of the activeHref. It is useful for dropdowns that are not direct children of the navigation menu.
 * @property {boolean} exactHref - Whether the navigation item should be active if the href is exactly the same as the activeHref.
 *
 * @event {{ href: string, event: Event }} dds-navigation-item-click - Fired when a navigation item is clicked.
 * @event {{ item: DapDSNavigationMenuItem }} dds-navigation-dropdown-open - Fired when a navigation dropdown is opened.
 *
 * @slot trigger - The trigger element (link, button, etc.) for this navigation item.
 * @slot title - The title of the navigation item.
 * @slot - The dropdown content (only used if trigger slot is provided).
 *
 * @csspart base - The base part of the navigation item.
 * @csspart trigger - The trigger element part.
 * @csspart content - The dropdown content part.
 */
@customElement('dap-ds-navigation-menu-item')
export default class DapDSNavigationMenuItem extends DapDSPopup {
  constructor() {
    super()
    this.placement = 'bottom-start'
    this.overflow = true
    this.floatingStrategy = 'fixed'
  }

  static readonly styles = unsafeCSS(`${styles}${popupStyles}`)

  @queryAssignedElements({ slot: 'trigger' })
  private readonly _assignedTrigger!: HTMLElement[]

  /** The name of the icon to display in the trigger. */
  @property({ type: String, reflect: true }) icon?: string

  /** ID of element that labels the navigation dropdown. */
  @property({ type: String, attribute: 'aria-labelledby' })
  ariaLabelledBy?: string

  /** The href of the navigation item that is active. */
  @property({ type: String, reflect: true }) activeHref?: string

  /** The href of the navigation item. If provided, the navigation item will be active if the href is a substring of the activeHref. It is useful for dropdowns that are not direct children of the navigation menu. */
  @property({ type: String, reflect: true }) baseHref?: string

  /** Whether the navigation item should be active if the href is exactly the same as the activeHref. */
  @property({ type: Boolean, reflect: true }) exactHref = false

  @state() private hasContent = false
  @state() private _lastClickedItem: HTMLElement | null = null
  @state() private active = false

  private _keyboardNavigation: ReturnType<
    typeof createKeyboardNavigationHandler
  > | null = null

  private get _trigger(): HTMLElement[] {
    // If there are assigned elements in the trigger slot, use those
    if (this._assignedTrigger && this._assignedTrigger.length > 0) {
      return this._assignedTrigger
    }
    // Otherwise, find the default button in the shadow DOM
    const defaultButton = this.shadowRoot?.querySelector(
      'slot[name="trigger"] dap-ds-button',
    )
    return defaultButton ? [defaultButton as HTMLElement] : []
  }

  async connectedCallback() {
    await super.connectedCallback()
    this.addEventListener('click', this._handleClick)
    this.addEventListener('keydown', this._handleKeyDown)
    this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this._handleClick)
    this.removeEventListener('keydown', this._handleKeyDown)
    document.removeEventListener('mousedown', this.handleDocumentMouseDown)
  }

  async updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    if (changedProperties.has('opened') && this.opened && this.hasContent) {
      this._initializeKeyboardNavigation()
    }

    if (changedProperties.has('activeHref')) {
      this._updateActiveState()
    }
  }

  private _handleClick = (event: Event) => {
    if (this.hasContent) {
      const target = event.target as HTMLElement
      const triggerElements = this._trigger
      const isClickOnTrigger =
        target === this ||
        triggerElements.some(
          trigger => trigger === target || trigger.contains(target),
        )

      if (isClickOnTrigger) {
        event.preventDefault()
        event.stopPropagation()
        this.opened ? this.hideDropDown() : this.showDropDown()
      }
    } else {
      const triggerElements = this._trigger
      const href = triggerElements[0]?.getAttribute('href')
      if (href) {
        this.emit('dds-navigation-item-click', {
          href,
          event,
        })
      }
    }
  }

  private _handleKeyDown = (event: KeyboardEvent) => {
    if (!this.hasContent) {
      // For simple navigation items, handle Enter/Space
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        const triggerElements = this._trigger
        triggerElements[0]?.click()
      }
      return
    }

    // Handle dropdown keyboard navigation
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
      case 'Enter':
      case ' ':
        if (!this.opened) {
          if (
            event.key === 'ArrowDown' ||
            event.key === 'ArrowUp' ||
            event.key === 'Enter' ||
            event.key === ' '
          ) {
            event.preventDefault()
            this.showDropDown()
          }
          return
        }

        if (this._keyboardNavigation?.handleKeyDown(event)) {
          // Key was handled by navigation manager
          if (event.key === 'Enter' || event.key === ' ') {
            this._handleFocusOut()
          }
        }
        break

      case 'Tab':
        if (this.opened) {
          // Handle Tab navigation when dropdown is open
          if (this._keyboardNavigation?.handleKeyDown(event)) {
            // Tab navigation was handled by the keyboard navigation manager
            // No need to close the dropdown, just let navigation continue
          }
        }
        break

      case 'Escape':
        event.preventDefault()
        this._handleFocusOut()
        break
    }
  }

  private handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) {
      this.hideDropDown()
    }
  }

  private _initializeKeyboardNavigation() {
    if (!this.hasContent) return

    this._keyboardNavigation = createKeyboardNavigationHandler(this, {
      enabledSelector:
        'dap-ds-button:not([disabled]), dap-ds-link:not([disabled]), dap-ds-navigation-menu-item:not([disabled]), a:not([disabled]), button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="menuitem"]:not([disabled]), dap-ds-command-item:not([disabled])',
      onItemClick: item => {
        this._lastClickedItem = item
      },
    })

    // Initialize focus after next update
    this.updateComplete.then(() => {
      this._keyboardNavigation?.initializeFocus(
        this._lastClickedItem || undefined,
      )
    })
  }

  private _handleFocusOut() {
    this.hideDropDown()
    this.focus()
  }

  public focus() {
    const triggerElements = this._trigger
    if (triggerElements && triggerElements.length > 0) {
      triggerElements[0].focus()
    }
  }

  async showDropDown() {
    if (this.opened || !this.hasContent) {
      return
    }

    this.emit('dds-navigation-dropdown-open', { item: this })

    this.opened = true
    const triggerElements = this._trigger
    triggerElements[0]?.setAttribute('aria-expanded', 'true')

    document.addEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
  }

  async hideDropDown() {
    if (!this.opened) {
      return
    }

    this.opened = false
    const triggerElements = this._trigger
    triggerElements[0]?.setAttribute('aria-expanded', 'false')

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    this._keyboardNavigation?.reset()

    await this.updateComplete
  }

  public _handleTriggerSlotChangeCallback() {
    this.updateComplete.then(() => {
      const triggerElements = this._trigger
      if (triggerElements && triggerElements.length > 0) {
        triggerElements[0].setAttribute('aria-controls', 'dropdown')
        triggerElements[0].setAttribute('aria-expanded', 'false')
        triggerElements[0].setAttribute(
          'aria-haspopup',
          this.hasContent ? 'menu' : 'false',
        )
        triggerElements[0].style.display = 'flex'
        this._updateActiveState()
      }
    })
  }

  private _updateActiveState() {
    const triggerElements = this._trigger
    if (
      (!triggerElements || triggerElements.length === 0 || !this.activeHref) &&
      !this.baseHref
    ) {
      this.active = false
    } else if (this.baseHref) {
      if (this.exactHref) {
        this.active = this.baseHref === this.activeHref
      } else {
        this.active = this.activeHref?.startsWith(this.baseHref) || false
      }
    } else {
      const triggerHref = triggerElements[0].getAttribute('href')
      this.active = triggerHref === this.activeHref
      ;(triggerElements[0] as any).active = this.active
    }
  }

  public _handleContentSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement
    if (slot) {
      const childNodes = slot.assignedElements()
      this.hasContent = childNodes.length > 0
    }

    this._keyboardNavigation?.invalidateCache()
  }

  renderTrigger() {
    return html`
      <dap-ds-button
        variant="subtle-menu"
        ?active=${this.active}
        aria-expanded=${this.opened ? 'true' : 'false'}
        part="trigger">
        <dap-ds-stack direction="row">
          <slot name="title"></slot>
          <dap-ds-icon
            name=${this.icon || 'arrow-down-s-line'}
            aria-hidden="true"></dap-ds-icon>
        </dap-ds-stack>
      </dap-ds-button>
    `
  }

  render() {
    return html`
      <li
        part="base"
        class=${clsx('navigation-item', {
          'navigation-item--has-content': this.hasContent,
          'navigation-item--opened': this.opened,
        })}>
        ${this.renderPopup()}
      </li>
    `
  }
}
